import { queryOptions } from '@tanstack/react-query'
import {
  applyConversationPoolToParams,
  type ConversationPool,
} from '@/lib/inbox/conversation-pool-param'
import { keepPreviousWorkspaceData } from './keep-previous-workspace-data'
import { purgeInboxV2PersistedCacheOnAccessError } from './persist'

import {
  applyInboxV2FiltersToParams,
  type InboxV2FilterQuery,
  type InboxV2ListResponse,
  inboxV2FiltersSignature,
} from './types'

// biome-ignore lint/performance/noBarrelFile: Preserve existing consumers while the server imports the dependency-free key module directly.
export {
  INBOX_LIST_PAGE_SIZE,
  inboxV2QueryKey,
  type UseInboxListOpts,
} from './inbox-list-query-key'
import type { UseInboxListOpts } from './inbox-list-query-key'

export interface InboxV2Counts {
  archived: number
  done: number
  follow_up: number
  todo: number
  total: number
}

interface InboxV2PropertyCovers {
  covers: Record<string, string | null>
}

const INBOX_V2_LIST_KEY_LENGTH = 7
const SEARCH_FILTER_PATTERN = /(^|&)q=/
const INBOX_V2_LIST_STATUSES: ReadonlySet<string> = new Set([
  'ALL',
  'TODO',
  'FOLLOW_UP',
  'DONE',
  'ARCHIVED',
  'DRAFTS',
])

/** True when `key` is an inbox-v2 LIST key for `workspaceId` (the shape built by
 *  `inboxV2QueryKey`). The conversation-detail family
 *  `['inbox-v2', 'conversation', …]` never matches: its second element is the
 *  literal `'conversation'` and its third is not a status param. */
export function isInboxV2ListKey(key: readonly unknown[], workspaceId: string): boolean {
  return (
    key.length === INBOX_V2_LIST_KEY_LENGTH &&
    key[0] === 'inbox-v2' &&
    key[1] === workspaceId &&
    typeof key[2] === 'string' &&
    INBOX_V2_LIST_STATUSES.has(key[2])
  )
}

export function isInboxV2PoolListKey(
  key: readonly unknown[],
  workspaceId: string,
  pool: ConversationPool
): boolean {
  return pool === 'labs'
    ? key.length === INBOX_V2_LIST_KEY_LENGTH + 1 &&
        key[INBOX_V2_LIST_KEY_LENGTH] === 'labs' &&
        isInboxV2ListKey(key.slice(0, INBOX_V2_LIST_KEY_LENGTH), workspaceId)
    : isInboxV2ListKey(key, workspaceId)
}

export function isLabsPoolKey(key: readonly unknown[], workspaceId: string): boolean {
  return (
    (key[0] === 'inbox-v2' || key[0] === 'inbox-v2-counts') &&
    key[1] === workspaceId &&
    key.at(-1) === 'labs'
  )
}

/** Status param (`'ALL' | 'TODO' | …`) of a key that passed `isInboxV2ListKey`. */
export function listKeyStatus(key: readonly unknown[]): string {
  const [, , status] = key
  return typeof status === 'string' ? status : 'ALL'
}

/** Saved-view id of a key that passed `isInboxV2ListKey`, or the empty string
 *  for an unscoped list. */
export function listKeyViewId(key: readonly unknown[]): string {
  const [, , , , viewId] = key
  return typeof viewId === 'string' ? viewId : ''
}

/** True when the list key carries a `?q=` search. Search pages prefer
 *  `latest_event.*` server-side, so realtime patches must skip them. */
export function listKeyHasSearch(key: readonly unknown[]): boolean {
  const signature = key[INBOX_V2_LIST_KEY_LENGTH - 1]
  return typeof signature === 'string' && SEARCH_FILTER_PATTERN.test(signature)
}

/** True when any server-evaluated list filter is encoded in the key. */
export function listKeyHasFilters(key: readonly unknown[]): boolean {
  const signature = key[INBOX_V2_LIST_KEY_LENGTH - 1]
  return typeof signature === 'string' && signature.length > 0
}

/** True when the list key is scoped by an inbox slug or a saved view. */
export function listKeyHasInboxOrViewFilter(key: readonly unknown[]): boolean {
  const [, , , inbox, viewId] = key
  return (
    (typeof inbox === 'string' && inbox.length > 0) ||
    (typeof viewId === 'string' && viewId.length > 0)
  )
}

function buildInboxV2ListParams(opts: UseInboxListOpts = {}, pageParam = 0) {
  const { status = 'ALL', inbox, viewId, limit = 50, filters } = opts
  const params = new URLSearchParams({
    status,
    limit: String(limit),
    offset: String(pageParam),
  })
  if (inbox) {
    params.set('inbox', inbox)
  }
  if (viewId) {
    params.set('view', viewId)
  }
  applyInboxV2FiltersToParams(params, filters)
  applyConversationPoolToParams(params, opts.pool)
  return params
}

/** One offset-paged page from the legacy inbox-v2 route. Page assembly lives in
 *  `inbox-list-v1.ts`, which picks between this and the `/api/v1` source. */
export async function fetchInboxV2ListPage(
  workspaceId: string,
  opts: UseInboxListOpts,
  offset: number,
  signal: AbortSignal
): Promise<InboxV2ListResponse> {
  const params = buildInboxV2ListParams(opts, offset)
  const res = await fetch(
    `/api/workspaces/${workspaceId}/inbox-v2/conversations?${params.toString()}`,
    { credentials: 'include', signal }
  )
  purgeInboxV2PersistedCacheOnAccessError(workspaceId, res.status)
  if (!res.ok) {
    throw new Error(`inbox-v2 list failed: ${res.status}`)
  }
  return res.json()
}

export function inboxCountsQueryKey(
  workspaceId: string,
  inbox?: string,
  filters?: InboxV2FilterQuery,
  viewId?: string,
  pool?: ConversationPool
) {
  const key = [
    'inbox-v2-counts',
    workspaceId,
    inbox ?? '',
    viewId ?? '',
    inboxV2FiltersSignature(filters),
  ] as const
  return pool === 'labs' ? ([...key, 'labs'] as const) : key
}

export function buildInboxCountsParams(
  inbox?: string,
  filters?: InboxV2FilterQuery,
  viewId?: string,
  pool?: ConversationPool
) {
  const params = new URLSearchParams()
  if (inbox) {
    params.set('inbox', inbox)
  }
  if (viewId) {
    params.set('view', viewId)
  }
  applyInboxV2FiltersToParams(params, filters)
  applyConversationPoolToParams(params, pool)
  return params
}

export function inboxV2PropertyCoversQueryOptions(workspaceId: string, propertyIds: string[]) {
  const normalizedIds = Array.from(new Set(propertyIds)).sort()

  return queryOptions<InboxV2PropertyCovers>({
    queryKey: ['inbox-v2-property-covers', workspaceId, normalizedIds],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams()
      if (normalizedIds.length > 0) {
        params.set('ids', normalizedIds.join(','))
      }
      const res = await fetch(
        `/api/workspaces/${workspaceId}/inbox-v2/property-covers?${params.toString()}`,
        { credentials: 'include', signal }
      )
      purgeInboxV2PersistedCacheOnAccessError(workspaceId, res.status)
      if (!res.ok) {
        throw new Error(`inbox-v2 property covers failed: ${res.status}`)
      }
      return res.json()
    },
    enabled: Boolean(workspaceId) && normalizedIds.length > 0,
    placeholderData: keepPreviousWorkspaceData(workspaceId),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // Persisted family (local-first): opt back into offlineFirst.
    networkMode: 'offlineFirst',
  })
}
